import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import MessageBox from "./MessageBox";
import api from "../services/api.service";
import * as Styles from "../styles/CancelOrder.Modal.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";
import { useParams } from "react-router-dom";

const CancelOrderModal: React.FC<{
  isOpen: boolean;
  onRequestClose: (clearField?: boolean) => void;
}> = ({ isOpen, onRequestClose }) => {
  const {
    subsidiaryDataZustand,
    setOrderDataZustand,
    orderDataZustand,
    authUserZustand,
    setMessageZustand,
  } = useCombinedStore((state) => {
    return {
      setSubsidiaryDataZustand: state.setSubsidiaryDataZustand,
      subsidiaryDataZustand: state.subsidiaryDataZustand,
      setOrderDataZustand: state.setOrderDataZustand,
      orderDataZustand: state.orderDataZustand,
      setMessageZustand: state.setMessageZustand,
      authUserZustand: state.authUserZustand,
    };
  });
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const { saleOption } = useParams();
  const selectedSaleOption =
    subsidiaryDataZustand.selectedPdv.saleOptions.filter(
      (item) => item.itemInfoUuid === saleOption
    )[0];

  const selectedProducts = orderDataZustand.products?.filter(
    (product: any) => product.productSelected || product.productSemiSelected
  );

  const selectedObservations = orderDataZustand.products?.flatMap(
    (product: any) => (product.productObservations || []).filter(
      (observation: any) => observation.observationSelected
    )
  ) || [];

  const selectedProductsAndObservations = orderDataZustand.products?.filter(
    (product: any) =>
      product.productSelected ||
      product.productSemiSelected ||
      (product.productObservations || []).some(
        (observation: any) => observation.observationSelected
      )
  );

  let modalTitle = "";
  let modalDescription = "";
  let modalButtonTitle = "";

  if ((orderDataZustand.orderStatus ?? 0) >= 3 &&
    orderDataZustand.orderType === "3") {
    modalTitle = "Cancelar Pedido";
    modalDescription = "Deseja cancelar o pedido?";
    modalButtonTitle = "Cancelar Pedido";

  }
  else if (selectedProducts && selectedObservations) {
    if (selectedProducts?.length === 1 && selectedObservations?.length === 0) {
      modalTitle = "Cancelar Produto";
      modalDescription = "Deseja cancelar o produto selecionado?";
      modalButtonTitle = "Cancelar Produto";
    } else if ((selectedProducts?.length ?? 0) > 1 && selectedObservations?.length === 0) {
      modalTitle = "Cancelar Produtos";
      modalDescription = "Deseja cancelar os produtos selecionados?";
      modalButtonTitle = "Cancelar Produtos";
    } else if (selectedObservations?.length === 1) {
      modalTitle = "Cancelar Observação";
      modalDescription = "Deseja cancelar a observação selecionada?";
      modalButtonTitle = "Cancelar Observação";
    } else if (selectedObservations?.length > 1) {
      modalTitle = `Cancelar Observações`;
      modalDescription = "Deseja cancelar as observações selecionadas?";
      modalButtonTitle = "Cancelar Observações";
    } else {
      modalTitle = "Cancelar Conta";
      modalDescription = "Deseja cancelar a conta?";
      modalButtonTitle = "Cancelar Conta";
    }
  }

  const [selectedCancelReason, setSelectedCancelReason] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Se já estiver enviando, não faça nada
    if (isSubmitting) return;

    setIsSubmitting(true); // Marca que iniciou a submissão
    try {
      if (selectedCancelReason === "" && selectedProductsAndObservations) {
        setMessageZustand(`Selecione um motivo de estorno!`, `error`);
        return;
      }

      if ((orderDataZustand.orderStatus ?? 0) >= 2 &&
        orderDataZustand.orderType === "3") {
        const productsPayload = orderDataZustand?.products?.map(product => {
          const hasSelectedObservations = product.productObservations?.some((obs: any) => obs.observationSelected);
          return {
            orderInfoUuid: product.productOrderUuid,
            cancelReasonUuid: selectedCancelReason,
            productProduced: true,
            observations: hasSelectedObservations
              ? product.productObservations?.filter((obs: any) => obs.observationSelected)
                .map((obs: any) => ({
                  productObservationUuid: obs.observationUuid,
                  productObservationProduced: obs.productObservationProduced || false
                }))
              : []
          };
        });

        try {
          // Novo endpoint (ou o mesmo) adaptado para receber array no body
          await api.post(`removeProductOrder/${orderDataZustand.orderUuid}/${selectedSaleOption?.itemInfoValue.saleOptionPrinterAddress}`, {
            products: productsPayload
          });
        } catch (error: any) {
          if (error.message === "Network Error") {
            setMessageZustand(
              `Erro ao tentar remover produtos/observações, entre em contato com os administradores do Alfredo da sua unidade.`,
              `error`
            );
          } else {
            setMessageZustand(
              `Erro ao remover produtos/observações: ${error.message}`,
              `error`
            );
          }
        }
      } else {
        if (selectedProductsAndObservations && selectedProductsAndObservations.length > 1) {
          // Montagem do payload em lote
          const productsPayload = selectedProductsAndObservations.map(product => {
            const hasSelectedObservations = product.productObservations?.some((obs: any) => obs.observationSelected);
            return {
              orderInfoUuid: product.productOrderUuid,
              cancelReasonUuid: selectedCancelReason,
              productProduced: product.productPrinted,
              observations: hasSelectedObservations
                ? product.productObservations?.filter((obs: any) => obs.observationSelected)
                  .map((obs: any) => ({
                    productObservationUuid: obs.observationUuid,
                    productObservationProduced: obs.productObservationProduced || false
                  }))
                : []
            };
          });

          try {
            // Novo endpoint (ou o mesmo) adaptado para receber array no body
            await api.post(`removeProductOrder/${orderDataZustand.orderUuid}/${selectedSaleOption?.itemInfoValue.saleOptionPrinterAddress}`, {
              products: productsPayload
            });
          } catch (error: any) {
            if (error.message === "Network Error") {
              setMessageZustand(
                `Erro ao tentar remover produtos/observações, entre em contato com os administradores do Alfredo da sua unidade.`,
                `error`
              );
            } else {
              setMessageZustand(
                `Erro ao remover produtos/observações: ${error.message}`,
                `error`
              );
            }
          }

        } else {
          // Mantém a lógica antiga caso seja apenas um produto/observação
          selectedProductsAndObservations?.forEach(async product => {
            const hasSelectedObservations = product.productObservations?.some((observation: any) => observation.observationSelected);
            if (product.productObservations && hasSelectedObservations) {
              for (const observation of product.productObservations) {
                if (!observation.observationSelected) continue;
                try {
                  await api.post(`removeProductObservation/${orderDataZustand.orderUuid}/${selectedSaleOption?.itemInfoValue.saleOptionPrinterAddress}`, {
                    productOrderUuid: String(product.productOrderUuid),
                    productObservationUuid: String(observation.observationUuid),
                    productObservationProduced: Boolean(observation.productObservationProduced || false),
                    cancelReasonUuid: String(selectedCancelReason),
                  });
                } catch (error: any) {
                  handleNetworkOrErrorMessage(error, "remover observações");
                }
              }
              // Após remover as observações, para a execução e não cancela a conta
              onRequestClose();
              setSelectedCancelReason("");
              setIsSubmitting(false);
              return;
            } else {

              try {
                // Filtra apenas as observações selecionadas e monta o array correto
                const selectedObservations = product.productObservations
                  ? product.productObservations
                    .filter((obs: any) => obs.observationSelected)
                    .map((obs: any) => ({
                      productObservationUuid: obs.observationUuid,
                      productObservationProduced: obs.productObservationProduced || false,
                    }))
                  : [];
                await api.post(`removeProductOrder/${orderDataZustand.orderUuid}/${selectedSaleOption?.itemInfoValue.saleOptionPrinterAddress}`, {
                  products: [
                    {
                      orderInfoUuid: product.productOrderUuid,
                      cancelReasonUuid: selectedCancelReason,
                      productProduced: product.productPrinted,
                      observations: selectedObservations, // Envia apenas as observações selecionadas
                    },
                  ],
                });
              } catch (error: any) {
                handleNetworkOrErrorMessage(error, "remover observações");
              }
            }
          });
        }
      }
      if (selectedProducts?.length === orderDataZustand.products?.length || ((orderDataZustand.orderStatus ?? 0) >= 2 &&
        orderDataZustand.orderType === "3")) {
        try {
          await api.post(`cancelOrder/${orderDataZustand.orderUuid}`);
        } catch (error: any) {
          handleNetworkOrErrorMessage(error, "cancelar o pedido");
        }
        onRequestClose(true);
        setSelectedCancelReason("");
        return;
      }
      onRequestClose();
      setSelectedCancelReason("");
      // ou onRequestClose(true); — dependendo do seu fluxo
    } catch (error: any) {
      // Trate seu erro
      setMessageZustand(`Erro: ${error.message}`, "error");
    } finally {
      // Libera para que o usuário possa tentar de novo se precisar
      setIsSubmitting(false);
    }
  };
  function handleNetworkOrErrorMessage(error: any, action: string) {
    if (error.message === "Network Error") {
      setMessageZustand(
        `Erro ao tentar ${action}, entre em contato com os administradores do Alfredo da sua unidade.`,
        `error`
      );
    } else {
      setMessageZustand(
        `Erro ao ${action}: ${error.message}`,
        `error`
      );
    }
  }


  useEffect(() => { }, [
    selectedProducts,
    selectedObservations,
    selectedCancelReason,
    setOrderDataZustand,
    orderDataZustand,
  ]);

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      await handleSubmit();
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        setSelectedCancelReason("");
        onRequestClose();
      }}
      style={Styles.customStyles}
      contentLabel="Main Modal"
      ariaHideApp={false}
    >
      <Styles.ModalWrapper
        tabIndex={-1}
        ref={modalWrapperRef}
        onKeyDown={handleKeyDown}
      >
        <Styles.CloseButton
          onClick={() => {
            setSelectedCancelReason("");
            onRequestClose();
          }}
        >
          &times;
        </Styles.CloseButton>
        <Styles.ModalContent>
          <Styles.ModalHeader
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
            <Styles.ModalTitle>
              {modalTitle}
            </Styles.ModalTitle>
            <Styles.ModalDescription>
              {modalDescription}
            </Styles.ModalDescription>
          </Styles.ModalHeader>
          <Styles.ModalBody
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .backgroundColor
            }
          >
            {(orderDataZustand.orderStatus ?? 0) < 3 &&
              orderDataZustand.orderType !== "3" && (
                <Styles.RowContainer>
                  <Styles.TableContainer>
                    <Styles.ProductsTable>
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Nome</th>
                          <th>
                            <input
                              type="checkbox"
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const isChecked = e.target.checked;
                                // Atualiza apenas o campo productPrinted dos produtos selecionados,
                                // sem alterar a estrutura original dos produtos no orderDataZustand
                                const updatedProducts = orderDataZustand.products?.map((product: any) => {
                                  // Só altera se o produto está em selectedProductsAndObservations
                                  const isSelected = selectedProductsAndObservations?.some(
                                    (p: any) => p.productOrderUuid === product.productOrderUuid
                                  );
                                  if (isSelected) {
                                    return {
                                      ...product,
                                      productPrinted: isChecked,
                                    };
                                  }
                                  return product;
                                });
                                setOrderDataZustand({
                                  ...orderDataZustand,
                                  products: updatedProducts,
                                });
                              }}
                              checked={
                                selectedProductsAndObservations?.length
                                  ? selectedProductsAndObservations.every((product: any) =>
                                    product.productPrinted
                                  )
                                  : false
                              }
                            />
                            {""} - Produzido?
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {selectedProductsAndObservations?.map((product: any, index: number) => (
                          <React.Fragment key={product.productOrderUuid}>
                            <tr
                              className={index === product.productSelected ? "selected" : ""}
                            >
                              <td>{product.productCode}</td>
                              <td>{product.productName}</td>
                              <td>
                                {(!product.productObservations?.length || product.productObservations.every((obs: { observationSelected: any; }) => !obs.observationSelected)) && (
                                  <input
                                    type="checkbox"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                      const isChecked = e.target.checked;
                                      // Atualiza apenas o campo productPrinted do produto correspondente,
                                      // sem alterar a estrutura original dos produtos no orderDataZustand
                                      const updatedProducts = orderDataZustand.products?.map((prod: any) => {
                                        if (prod.productOrderUuid === product.productOrderUuid) {
                                          return {
                                            ...prod,
                                            productPrinted: isChecked,
                                          };
                                        }
                                        return prod;
                                      });
                                      setOrderDataZustand({
                                        ...orderDataZustand,
                                        products: updatedProducts,
                                      });
                                    }}
                                    checked={product.productPrinted}
                                  />
                                )}
                              </td>
                            </tr>

                            {product.productObservations?.filter((observation: any) => observation.observationSelected).map((observation: any, obsIndex: number) => (
                              <React.Fragment key={observation.productObservationUuid}>
                                {obsIndex === 0 && (
                                  <tr style={{ border: 'none' }}>
                                    <td colSpan={3}>Observações:</td>
                                  </tr>
                                )}
                                <tr style={{ border: 'none' }}>
                                  <td style={{ border: 'none' }}>{observation.observationName}</td>
                                  <td style={{ border: 'none' }}></td>
                                  <td style={{ border: 'none' }}>
                                    <input
                                      type="checkbox"
                                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        const isChecked = e.target.checked;
                                        // Atualiza apenas o campo productObservationProduced da observação correspondente,
                                        // sem alterar a estrutura original dos produtos no orderDataZustand
                                        const updatedProducts = orderDataZustand.products?.map((prod: any) => {
                                          if (prod.productOrderUuid === product.productOrderUuid) {
                                            return {
                                              ...prod,
                                              productObservations: prod.productObservations?.map((obs: any) => {
                                                if (obs.observationUuid === observation.observationUuid) {
                                                  return {
                                                    ...obs,
                                                    productObservationProduced: isChecked,
                                                  };
                                                }
                                                return obs;
                                              }),
                                            };
                                          }
                                          return prod;
                                        });
                                        setOrderDataZustand({
                                          ...orderDataZustand,
                                          products: updatedProducts,
                                        });
                                      }}
                                      checked={observation.productObservationProduced}
                                    />
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Styles.ProductsTable>
                  </Styles.TableContainer>
                </Styles.RowContainer>
              )}
            <Styles.RowContainer>
              <Styles.DropdownInput
                color={
                  subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                    .buttonsColor
                }
              >
                <select
                  onChange={(e) => setSelectedCancelReason(e.target.value)}
                >
                  <option value="">Selecione</option>
                  {subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodCancelReasons?.CancelReasons?.map(
                    (cancelReason: any, index: number) => (
                      <option key={index} value={cancelReason.cancelReasonUuid}>
                        {cancelReason.cancelReasonName}
                      </option>
                    )
                  )}
                </select>
                <label>Motivos</label>
              </Styles.DropdownInput>
            </Styles.RowContainer>
          </Styles.ModalBody>
          <Styles.ModalFooter
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Button
              onClick={() => {
                {
                  setSelectedCancelReason("");
                  onRequestClose();
                }
              }}
            >
              Cancelar
            </Styles.Button>
            <Styles.Button onClick={handleSubmit}>
              {modalButtonTitle}
            </Styles.Button>
          </Styles.ModalFooter>
        </Styles.ModalContent>
      </Styles.ModalWrapper>
    </Modal>
  );
};

export default CancelOrderModal;
